import { useContext, useEffect, useState } from "react"
import { GetStaticProps, NextPage } from "next"
import Head from "next/head"
import { isEmpty } from "lodash"
import * as Sentry from "@sentry/nextjs"
import Header from "@modules/Common/components/Header"
import { Page } from "@styled/dashboard"
import { Container } from "react-bootstrap"
import ProductBanners from "@modules/Product/components/ProductBanners"
import ProductList from "@modules/Product/components/ProductList"
import { StyledHeader } from "@modules/Product/styled/StyledProductCard"
import { PRODUCT_PAGE_DATA } from "@modules/Product/graphql"
import { AppContext } from "AppContext"
import { initializeApollo } from "apollo/client"
import { DEFAULT_META_IMAGE_URL } from "@utils/constant"
import { ProductPageDataQuery } from "@generated/graphql"
import ProductCheckoutModal from "@modules/Product/components/ProductCheckoutModal"
import ShopifyComponent from "@modules/Common/components/ShopifyComponent"

interface IProductsProps {
  banners: ProductPageDataQuery["publicProductBanners"],
  newReleasedProducts: ProductPageDataQuery["newReleasedProducts"],
}

const productShopifyScript = `
<div id='collection-component-1692641212865'></div>
<script type="text/javascript">
(function () {
  var scriptURL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';
  if (window.ShopifyBuy) {
    if (window.ShopifyBuy.UI) {
      ShopifyBuyInit();
    } else {
      loadScript();
    }
  } else {
    loadScript();
  }
  function loadScript() {
    var script = document.createElement('script');
    script.async = true;
    script.src = scriptURL;
    (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(script);
    script.onload = ShopifyBuyInit;
  }
  function ShopifyBuyInit() {
    var client = ShopifyBuy.buildClient({
      domain: 'mss-test-8599.myshopify.com',
      storefrontAccessToken: '50132e5bc03170b89d2d84e3324e0948',
    });
    ShopifyBuy.UI.onReady(client).then(function (ui) {
      ui.createComponent('collection', {
        id: '445362209089',
        node: document.getElementById('collection-component-1692641212865'),
        moneyFormat: 'Rs.%20%7B%7Bamount%7D%7D',
        options: {
  "product": {
    "styles": {
      "product": {
        "@media (min-width: 601px)": {
          "max-width": "calc(33.33333% - 30px)",
          "margin-left": "30px",
          "margin-bottom": "50px",
          "width": "calc(33.33333% - 30px)"
        },
        "img": {
          "height": "calc(100% - 15px)",
          "position": "absolute",
          "left": "0",
          "right": "0",
          "top": "0"
        },
        "imgWrapper": {
          "padding-top": "calc(75% + 15px)",
          "position": "relative",
          "height": "0"
        }
      },
      "title": {
        "font-size": "16px"
      },
      "button": {
        "color": "#000000",
        ":hover": {
          "color": "#000000",
          "background-color": "#e6c700"
        },
        "background-color": "#ffdd00",
        ":focus": {
          "background-color": "#e6c700"
        },
        "border-radius": "12px",
        "padding-left": "28px",
        "padding-right": "28px"
      }
    },
    "buttonDestination": "modal",
    "contents": {
      "options": false
    },
    "text": {
      "button": "Buy Now"
    }
  },
  "productSet": {
    "styles": {
      "products": {
        "@media (min-width: 601px)": {
          "margin-left": "-30px"
        }
      }
    }
  },
  "modalProduct": {
    "contents": {
      "img": false,
      "imgWithCarousel": true,
      "button": false,
      "buttonWithQuantity": true
    },
    "styles": {
      "product": {
        "@media (min-width: 601px)": {
          "max-width": "100%",
          "margin-left": "0px",
          "margin-bottom": "0px"
        }
      },
      "button": {
        "color": "#000000",
        ":hover": {
          "color": "#000000",
          "background-color": "#e6c700"
        },
        "background-color": "#ffdd00",
        ":focus": {
          "background-color": "#e6c700"
        },
        "border-radius": "12px",
        "padding-left": "28px",
        "padding-right": "28px"
      },
      "title": {
        "font-family": "Helvetica Neue, sans-serif",
        "font-weight": "bold",
        "font-size": "26px",
        "color": "#4c4c4c"
      },
      "price": {
        "font-size": "16px"
      },
      "compareAt": {
        "font-size": "13.6px"
      },
      "unitPrice": {
        "font-size": "13.6px"
      }
    },
    "text": {
      "button": "Add to cart"
    }
  },
  "option": {},
  "cart": {
    "styles": {
      "button": {
        "color": "#000000",
        ":hover": {
          "color": "#000000",
          "background-color": "#e6c700"
        },
        "background-color": "#ffdd00",
        ":focus": {
          "background-color": "#e6c700"
        },
        "border-radius": "12px"
      }
    },
    "text": {
      "total": "Subtotal",
      "button": "Checkout",
      "noteDescription": "Special instructions for the team. We'll try our best to fulfil your requirements."
    },
    "contents": {
      "note": true
    }
  },
  "toggle": {
    "styles": {
      "toggle": {
        "background-color": "#ffdd00",
        ":hover": {
          "background-color": "#e6c700"
        },
        ":focus": {
          "background-color": "#e6c700"
        }
      },
      "count": {
        "color": "#000000",
        ":hover": {
          "color": "#000000"
        }
      },
      "iconPath": {
        "fill": "#000000"
      }
    }
  }
},
      });
    });
  }
})();
</script>
`

const ProductsPage: NextPage<IProductsProps> = ({ banners, newReleasedProducts }) => {
  const { state } = useContext(AppContext)
  const [renderProducts, setRenderProducts] = useState(false)

  const title = "Products for every age - Best skill kits for kids and adults for every age | MySkillShaala"
  const description = "Looking for a unique product to help you upskill? Our DIY Skill Kits are perfect for any age group and any occasion. There is a set for everyone, including adults. Search Kits here."
  const image = DEFAULT_META_IMAGE_URL

  useEffect(() => {
    setRenderProducts(true)
  }, [])

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/products`} />
        <meta property="og:site_name" content={process.env.NEXT_PUBLIC_SITE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/products`} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={image} />
      </Head>
      <Page>
        <Header showCoins={isEmpty(state.user) ? "false" : "true"} />
        <Container>
          <ProductBanners banners={banners} />
          <br />
          <StyledHeader><span>Rewards</span></StyledHeader>
          <br />
          <ProductList products={newReleasedProducts} />
          <br />
          <StyledHeader><span>All Products</span></StyledHeader>
          <br />
          {renderProducts && <ShopifyComponent htmlString={productShopifyScript} />}
          <ProductCheckoutModal />
        </Container>
      </Page>
    </>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const apolloClient = initializeApollo()
  const { data, error } = await apolloClient.query<ProductPageDataQuery>({
    query: PRODUCT_PAGE_DATA
  })
  if (data) {
    return {
      props: {
        banners: data.publicProductBanners,
        newReleasedProducts: data.newReleasedProducts,
        initialApolloState: apolloClient.cache.extract(),
      },
      revalidate: 600
    }
  } else {
    Sentry.captureException(error)
    return {
      props: {
        error: true
      }
    }
  }
}

export default ProductsPage
